import AsyncStorage from '@react-native-async-storage/async-storage';
import type { ChatSendAttempt } from './sendAttempt';

/**
 * Черновики сообщений: набранный, но не отправленный текст переживает выход из
 * треда — при возврате он снова в композере, а строка диалога показывает
 * «Черновик: …» (как в Telegram).
 *
 * Ключ — тот же conversation key `${entityType}:${entityId}`, что и у
 * hiddenChats/clearedChats. In-memory Map для синхронных чтений (рендер списка,
 * сид композера), персист в AsyncStorage с дебаунсом — пишем не на каждую
 * клавишу, а раз в паузу набора; очистка (отправка) сбрасывается сразу.
 */
const KEY = 'chat_drafts';
const ATTEMPTS_KEY = 'chat_draft_send_attempts';
const FLUSH_DELAY_MS = 400;

// CORE-1791 (возврат QA 28.10) — отправка, текст которой вернулся в черновик
// (`ChatSendAttempt`), живёт рядом с черновиком и так же переживает
// перезагрузку: после неё в поле тот же текст, и если задержанная отправка
// всё-таки дошла, повтор с этим ключом не создаст вторую копию, а приехавшее с
// историей сообщение уберёт текст из поля.

let cache = new Map<string, string>();
let attempts = new Map<string, ChatSendAttempt>();
let loaded = false;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function isSendAttempt(v: unknown): v is ChatSendAttempt {
  const a = v as ChatSendAttempt | null;
  return !!a && typeof a.clientMessageId === 'string' && !!a.clientMessageId && typeof a.text === 'string'
    // CORE-1791 (возврат QA 31.10) — файлы и цитата попытки; записи до правки их не знают.
    && (a.attachments === undefined || (Array.isArray(a.attachments) && a.attachments.every((f) => typeof f === 'string')))
    && (a.replyTo === undefined || typeof a.replyTo === 'string');
}

/** Load persisted drafts into memory (call once early). */
export async function loadChatDrafts(): Promise<void> {
  if (loaded) return;
  loaded = true;
  try {
    const raw = await AsyncStorage.getItem(KEY);
    if (raw) cache = new Map<string, string>(Object.entries(JSON.parse(raw)));
  } catch {
    /* first run / parse error — empty map */
  }
  try {
    const raw = await AsyncStorage.getItem(ATTEMPTS_KEY);
    if (raw) {
      attempts = new Map(
        Object.entries(JSON.parse(raw) as Record<string, unknown>)
          .filter((e): e is [string, ChatSendAttempt] => isSendAttempt(e[1])),
      );
    }
  } catch {
    /* first run / parse error — no attempts */
  }
}

/** Sync read — the unsent draft for this conversation ('' if none). */
export function getChatDraft(key: string): string {
  return cache.get(key) ?? '';
}

/** Remember / drop the draft. Whitespace-only counts as «no draft». */
export function setChatDraft(key: string, text: string): void {
  const empty = !text.trim();
  if (!empty && cache.get(key) === text) return;
  if (empty && !cache.has(key)) return;
  if (empty) cache.delete(key);
  else cache.set(key, text);
  if (flushTimer) clearTimeout(flushTimer);
  // Очистку пишем немедленно: отправленное сообщение не должно «воскреснуть»
  // черновиком, если приложение убьют в окно дебаунса.
  flushTimer = setTimeout(persist, empty ? 0 : FLUSH_DELAY_MS);
}

/** CORE-1791 — неподтверждённая отправка этого диалога или `null`. */
export function getChatSendAttempt(key: string): ChatSendAttempt | null {
  return attempts.get(key) ?? null;
}

/** CORE-1791 — запомнить (или забыть) неподтверждённую отправку. Пишется сразу. */
export function setChatSendAttempt(key: string, attempt: ChatSendAttempt | null): void {
  const prev = attempts.get(key);
  if (!attempt && !prev) return;
  if (attempt && prev && prev.clientMessageId === attempt.clientMessageId && prev.text === attempt.text) return;
  if (attempt) attempts.set(key, attempt);
  else attempts.delete(key);
  void persistAttempts();
}

async function persist(): Promise<void> {
  flushTimer = null;
  try {
    await AsyncStorage.setItem(KEY, JSON.stringify(Object.fromEntries(cache)));
  } catch {
    /* persistence best-effort */
  }
}

async function persistAttempts(): Promise<void> {
  try {
    await AsyncStorage.setItem(ATTEMPTS_KEY, JSON.stringify(Object.fromEntries(attempts)));
  } catch {
    /* persistence best-effort */
  }
}

/**
 * CORE-1791 — владелец черновика в ключе (веб): `user_<id>|<entityType>:<id>`.
 * На вебе хранилище — localStorage браузера, которым может пользоваться не
 * один человек, а id собеседника бывает общим у всех (Агапелла), поэтому ключ
 * начинается с того, кто пишет. Нативный ключ — без владельца: устройство
 * одного человека.
 */
export function chatDraftOwner(userId: string): string {
  return `user_${String(userId).replace(/^user_/, '')}`;
}

export function chatDraftKey(userId: string, conversationKey: string): string {
  return `${chatDraftOwner(userId)}|${conversationKey}`;
}

/**
 * CORE-1791 — выход из аккаунта стирает свои неотправленные черновики: на общем
 * компьютере следующий человек не должен их увидеть. Свои — с префиксом своего
 * id (веб) и без владельца (натив). Черновики другого аккаунта с его префиксом
 * не трогаем — они не наши. Пишется сразу, без дебаунса: выход — последнее,
 * что успеет сделать вкладка или приложение.
 */
export async function clearChatDraftsOf(userId: string | null | undefined): Promise<void> {
  // Хранилище могло ещё не дочитаться (вышли, не открыв ни одного чата) —
  // тогда стирать надо записанное, а не пустой кэш.
  await loadChatDrafts();
  const owned = userId ? `${chatDraftOwner(userId)}|` : null;
  const mine = (key: string) => (owned && key.startsWith(owned)) || !key.includes('|');
  // CORE-1791 — вместе с черновиками и их неподтверждённые отправки.
  const ownAttempts = Array.from(attempts.keys()).filter(mine);
  if (ownAttempts.length) {
    ownAttempts.forEach((key) => attempts.delete(key));
    await persistAttempts();
  }
  let changed = false;
  for (const key of Array.from(cache.keys())) {
    if (mine(key)) {
      cache.delete(key);
      changed = true;
    }
  }
  if (!changed) return;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  await persist();
}
